import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import seasonalPlannerService from '../../services/seasonalPlannerService';

const MONTH_NAMES_ES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const MEAL_GLYPHS = { breakfast: '🌅', lunch: '🍲', dinner: '🌙' };

/**
 * SeasonalWeekPlanner — Códice Botánico edition.
 *
 * Wen-inspired weekly planner rendered as a botanical almanac: a fret-crested
 * control ledger, a batch-cooking "ladder" selector, phytochemical coverage
 * rendered as specimen tokens, and the week laid out as codex day-leaves.
 * Data flow and the seasonalPlannerService contract are unchanged.
 */
const SeasonalWeekPlanner = () => {
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [dietStyle, setDietStyle] = useState('omnivore');
  const [dislikesText, setDislikesText] = useState('');
  const [respectTrophology, setRespectTrophology] = useState(true);
  const [batchCookingLevel, setBatchCookingLevel] = useState(3);
  const [batchCookingLevels, setBatchCookingLevels] = useState([]);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    seasonalPlannerService.getBatchCookingLevels()
      .then(setBatchCookingLevels)
      .catch(() => {});
  }, []);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await seasonalPlannerService.generatePlan({
        month,
        dietStyle,
        respectTrophology,
        batchCookingLevel,
        dislikes: dislikesText.split(',').map((s) => s.trim()).filter(Boolean),
      });
      setPlan(result);
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo generar el plan');
    } finally {
      setLoading(false);
    }
  };

  const levelFallback = [
    { level: 1, short_es: 'Sin fuego' },
    { level: 2, short_es: 'Una llama' },
    { level: 3, short_es: 'Dos frentes' },
    { level: 4, short_es: 'Estufa + horno' },
    { level: 5, short_es: 'Método 3+2' },
  ];
  const levels = batchCookingLevels.length > 0 ? batchCookingLevels : levelFallback;
  const selectedLevelDetail = batchCookingLevels.find((l) => l.level === batchCookingLevel);

  return (
    <div style={styles.wrapper}>
      {/* Masthead */}
      <header style={styles.masthead}>
        <span className="cdx-kicker">Almanaque estacional</span>
        <h1 className="cdx-display" style={styles.title}>
          Mi semana <em style={styles.titleEm}>de temporada</em>
        </h1>
        <p style={styles.subtitle}>
          Una sesión de bases el domingo y comidas que las reutilizan toda la semana —
          rotando entre grupos fitoquímicos para variedad real, no solo visual.
        </p>
      </header>

      {/* Control ledger */}
      <section style={styles.ledger} className="cdx-grain">
        <div className="cdx-greca" style={styles.ledgerCrest} />
        <div style={styles.controls}>
          <label style={styles.controlGroup}>
            <span style={styles.controlLabel}>Mes</span>
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))} style={styles.select}>
              {MONTH_NAMES_ES.map((name, idx) => (
                <option key={idx} value={idx + 1}>{name}</option>
              ))}
            </select>
          </label>

          <label style={styles.controlGroup}>
            <span style={styles.controlLabel}>Estilo</span>
            <select value={dietStyle} onChange={(e) => setDietStyle(e.target.value)} style={styles.select}>
              <option value="omnivore">Omnívoro</option>
              <option value="vegetarian">Vegetariano</option>
              <option value="vegan">Vegano</option>
            </select>
          </label>

          <label style={{ ...styles.controlGroup, flex: 2, minWidth: 200 }}>
            <span style={styles.controlLabel}>No me gusta (separar por comas)</span>
            <input
              type="text"
              value={dislikesText}
              onChange={(e) => setDislikesText(e.target.value)}
              placeholder="cilantro, betabel…"
              style={styles.input}
            />
          </label>

          <label style={styles.checkbox}>
            <input
              type="checkbox"
              checked={respectTrophology}
              onChange={(e) => setRespectTrophology(e.target.checked)}
            />
            <span>Respetar trofología</span>
          </label>

          <button type="button" onClick={handleGenerate} disabled={loading} className="cdx-btn cdx-btn--primary" style={styles.generateBtn}>
            {loading ? 'Tejiendo el plan…' : '✦ Generar plan'}
          </button>
        </div>

        {/* Batch-cooking ladder */}
        <div style={styles.ladderWrap}>
          <span style={styles.ladderLabel}>Nivel de cocina por tandas</span>
          <div style={styles.ladder} role="radiogroup" aria-label="Nivel batch-cooking">
            {levels.map((lvl) => {
              const active = lvl.level === batchCookingLevel;
              return (
                <button
                  key={lvl.level}
                  type="button"
                  onClick={() => setBatchCookingLevel(lvl.level)}
                  role="radio"
                  aria-checked={active}
                  style={{
                    ...styles.rung,
                    ...(active ? styles.rungActive : {}),
                  }}
                >
                  <span style={styles.rungNum}>{lvl.level}</span>
                  <span style={styles.rungLabel}>{lvl.short_es}</span>
                </button>
              );
            })}
          </div>
          {selectedLevelDetail && (
            <p style={styles.levelHint}>
              <strong style={{ color: 'var(--cdx-cempa-deep)' }}>{selectedLevelDetail.label_es}.</strong>{' '}
              {selectedLevelDetail.description_es}
            </p>
          )}
        </div>
      </section>

      {error && <p style={styles.error}>⚠ {error}</p>}

      {plan && (
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Philosophy + coverage */}
          <div style={styles.philosophyBox} className="cdx-grain">
            <p style={styles.philosophyText}>{plan.philosophy}</p>
            <p style={styles.dateRange}>
              Semana del <strong>{plan.start_date}</strong> al <strong>{plan.end_date}</strong>
              {plan.batch_cooking_label_es && (
                <> · <strong style={{ color: 'var(--cdx-cempa-deep)' }}>{plan.batch_cooking_label_es}</strong></>
              )}
            </p>

            {plan.phytochemical_coverage && Object.keys(plan.phytochemical_coverage).length > 0 && (
              <div style={styles.coverageBox}>
                <span style={styles.coverageLabel}>Variedad bioquímica esta semana</span>
                <div style={styles.coverageRow}>
                  {Object.entries(plan.phytochemical_coverage)
                    .sort((a, b) => b[1] - a[1])
                    .map(([group, count]) => (
                      <span key={group} className="cdx-tag cdx-tag--turq">
                        {group} · {count}
                      </span>
                    ))}
                </div>
              </div>
            )}
          </div>

          <div style={styles.twoColumn}>
            {/* Aside: produce + bases */}
            <aside style={styles.aside} className="cdx-grain">
              <div className="cdx-greca--cempa cdx-greca" />
              <div style={styles.asideBody}>
                <h3 style={styles.asideTitle}>Productos en temporada</h3>
                <div style={styles.tags}>
                  {plan.seasonal_produce.map((p) => (
                    <span key={p} className="cdx-tag">{p}</span>
                  ))}
                </div>

                <h3 style={{ ...styles.asideTitle, marginTop: 22 }}>Bases del domingo</h3>
                <ul style={styles.basesList}>
                  {plan.sunday_bases.map((b, idx) => (
                    <li key={idx} style={styles.baseItem}>{b}</li>
                  ))}
                </ul>
              </div>
            </aside>

            {/* Day leaves */}
            <div style={styles.days}>
              {plan.days.map((day, i) => (
                <motion.article
                  key={day.date}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.04 }}
                  style={{ ...styles.dayCard, ...(day.is_prep_day ? styles.dayCardPrep : {}) }}
                >
                  <header style={styles.dayHeader}>
                    <h4 style={styles.dayWeekday}>{day.weekday}</h4>
                    {day.is_prep_day && <span style={styles.prepBadge}>✦ Día de bases</span>}
                    <span style={styles.dayDate}>{day.date}</span>
                  </header>
                  <MealRow glyph={MEAL_GLYPHS.breakfast} name="Desayuno" meal={day.breakfast} />
                  <MealRow glyph={MEAL_GLYPHS.lunch} name="Comida" meal={day.lunch} />
                  <MealRow glyph={MEAL_GLYPHS.dinner} name="Cena" meal={day.dinner} />
                </motion.article>
              ))}
            </div>
          </div>
        </motion.section>
      )}
    </div>
  );
};

const MealRow = ({ glyph, name, meal }) => (
  <div style={styles.mealRow}>
    <div style={styles.mealHeader}>
      <span style={styles.mealIcon}>{glyph}</span>
      <strong style={styles.mealName}>{name}</strong>
      {meal.technique && <span style={styles.mealTechnique}>{meal.technique}</span>}
    </div>
    <p style={styles.mealLabel}>{meal.label}</p>
    {meal.trophology_note && <p style={styles.mealNote}>{meal.trophology_note}</p>}
  </div>
);

const styles = {
  wrapper: {
    maxWidth: 1140,
    margin: '0 auto',
    padding: '32px 24px 64px',
    fontFamily: 'var(--cdx-font-body)',
    color: 'var(--cdx-ink)',
  },
  masthead: { marginBottom: 24, textAlign: 'center', maxWidth: 680, marginLeft: 'auto', marginRight: 'auto' },
  title: { margin: '8px 0 10px', fontSize: 42, lineHeight: 1.05 },
  titleEm: { fontStyle: 'italic', color: 'var(--cdx-jade)' },
  subtitle: { margin: 0, color: 'var(--cdx-ink-soft)', lineHeight: 1.6, fontSize: 15 },

  ledger: {
    position: 'relative',
    background: 'var(--cdx-paper-2)',
    border: '1px solid var(--cdx-paper-edge)',
    borderRadius: 'var(--cdx-r-lg)',
    overflow: 'hidden',
    boxShadow: 'var(--cdx-shadow-md)',
    marginBottom: 22,
  },
  ledgerCrest: { position: 'relative', zIndex: 1 },
  controls: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 14,
    alignItems: 'flex-end',
    padding: '18px 20px 8px',
    position: 'relative',
    zIndex: 1,
  },
  controlGroup: { display: 'flex', flexDirection: 'column', gap: 5, flex: 1, minWidth: 130 },
  controlLabel: {
    fontFamily: 'var(--cdx-font-mono)',
    fontSize: 10.5,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--cdx-copal)',
    fontWeight: 500,
  },
  select: {
    padding: '10px 12px',
    borderRadius: 'var(--cdx-r-md)',
    border: '1px solid var(--cdx-paper-edge)',
    background: 'var(--cdx-paper)',
    color: 'var(--cdx-ink)',
    fontSize: 14,
    fontFamily: 'var(--cdx-font-body)',
    cursor: 'pointer',
  },
  input: {
    padding: '10px 12px',
    borderRadius: 'var(--cdx-r-md)',
    border: '1px solid var(--cdx-paper-edge)',
    background: 'var(--cdx-paper)',
    color: 'var(--cdx-ink)',
    fontSize: 14,
    fontFamily: 'var(--cdx-font-body)',
  },
  checkbox: { display: 'flex', gap: 7, alignItems: 'center', fontSize: 13, color: 'var(--cdx-ink-soft)', paddingBottom: 10 },
  generateBtn: { padding: '11px 20px', fontSize: 14.5 },

  ladderWrap: { padding: '4px 20px 20px', position: 'relative', zIndex: 1 },
  ladderLabel: {
    display: 'block',
    fontFamily: 'var(--cdx-font-mono)',
    fontSize: 10.5,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--cdx-copal)',
    marginBottom: 8,
  },
  ladder: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  rung: {
    flex: 1,
    minWidth: 96,
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    padding: '9px 12px',
    borderRadius: 'var(--cdx-r-md)',
    border: '1px solid var(--cdx-paper-edge)',
    background: 'var(--cdx-paper)',
    cursor: 'pointer',
    transition: 'all 0.18s',
    textAlign: 'left',
  },
  rungActive: {
    background: 'linear-gradient(160deg, var(--cdx-jade-bright), var(--cdx-jade))',
    borderColor: 'var(--cdx-jade-deep)',
    boxShadow: 'var(--cdx-shadow-md)',
    transform: 'translateY(-2px)',
  },
  rungNum: {
    fontFamily: 'var(--cdx-font-display)',
    fontSize: 18,
    fontWeight: 600,
    color: 'inherit',
    width: 22,
    textAlign: 'center',
  },
  rungLabel: { fontSize: 12.5, fontWeight: 600 },

  levelHint: {
    margin: '14px 0 0',
    padding: '12px 15px',
    background: 'var(--cdx-cempa-wash)',
    borderLeft: '4px solid var(--cdx-cempasuchil)',
    borderRadius: 'var(--cdx-r-md)',
    fontSize: 13.5,
    color: 'var(--cdx-ink-soft)',
    lineHeight: 1.55,
  },

  error: {
    color: 'var(--cdx-jamaica)',
    padding: '12px 16px',
    background: 'var(--cdx-jamaica-wash)',
    border: '1px solid var(--cdx-jamaica-soft)',
    borderRadius: 'var(--cdx-r-md)',
    margin: '0 0 18px',
    fontSize: 14,
  },

  philosophyBox: {
    position: 'relative',
    padding: '18px 20px',
    background: 'var(--cdx-jade-wash)',
    borderLeft: '4px solid var(--cdx-jade)',
    borderRadius: 'var(--cdx-r-md)',
    marginBottom: 22,
  },
  philosophyText: { margin: 0, fontSize: 14, color: 'var(--cdx-jade-deep)', lineHeight: 1.6, fontStyle: 'italic' },
  dateRange: { margin: '10px 0 0', fontSize: 13.5, color: 'var(--cdx-ink-soft)' },
  coverageBox: { marginTop: 14, paddingTop: 14, borderTop: '1px dashed var(--cdx-agave)' },
  coverageLabel: {
    fontFamily: 'var(--cdx-font-mono)',
    fontSize: 10.5,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--cdx-turquesa)',
    display: 'block',
    marginBottom: 8,
  },
  coverageRow: { display: 'flex', flexWrap: 'wrap', gap: 7 },

  twoColumn: { display: 'grid', gridTemplateColumns: 'minmax(240px, 300px) 1fr', gap: 20, alignItems: 'start' },
  aside: {
    position: 'sticky',
    top: 16,
    background: 'var(--cdx-paper-2)',
    border: '1px solid var(--cdx-paper-edge)',
    borderRadius: 'var(--cdx-r-lg)',
    overflow: 'hidden',
    boxShadow: 'var(--cdx-shadow-sm)',
  },
  asideBody: { padding: '16px 18px 20px', position: 'relative', zIndex: 1 },
  asideTitle: {
    margin: '0 0 12px',
    fontFamily: 'var(--cdx-font-display)',
    fontSize: 17,
    fontWeight: 600,
    color: 'var(--cdx-jade-deep)',
  },
  tags: { display: 'flex', flexWrap: 'wrap', gap: 7 },
  basesList: { margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 },
  baseItem: {
    fontSize: 13.5,
    color: 'var(--cdx-ink-soft)',
    lineHeight: 1.45,
    paddingLeft: 18,
    position: 'relative',
  },

  days: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 },
  dayCard: {
    background: 'var(--cdx-paper)',
    border: '1px solid var(--cdx-paper-edge)',
    borderRadius: 'var(--cdx-r-lg)',
    padding: '14px 15px',
    boxShadow: 'var(--cdx-shadow-sm)',
  },
  dayCardPrep: {
    background: 'var(--cdx-cempa-wash)',
    borderColor: 'var(--cdx-cempasuchil)',
    boxShadow: '0 6px 18px rgba(224,137,46,0.18)',
  },
  dayHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    marginBottom: 12,
    paddingBottom: 10,
    borderBottom: '1px solid var(--cdx-paper-edge)',
  },
  dayWeekday: {
    margin: 0,
    fontFamily: 'var(--cdx-font-display)',
    fontSize: 17,
    fontWeight: 600,
    color: 'var(--cdx-ink)',
  },
  prepBadge: {
    fontFamily: 'var(--cdx-font-mono)',
    fontSize: 9.5,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#5A3208',
    background: 'rgba(224,137,46,0.28)',
    padding: '3px 8px',
    borderRadius: 999,
  },
  dayDate: { marginLeft: 'auto', fontFamily: 'var(--cdx-font-mono)', fontSize: 11, color: 'var(--cdx-ink-faint)' },

  mealRow: { padding: '8px 0', borderBottom: '1px dashed var(--cdx-paper-edge)' },
  mealHeader: { display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 },
  mealIcon: { fontSize: 14 },
  mealName: { fontSize: 12, color: 'var(--cdx-jade)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 },
  mealTechnique: {
    marginLeft: 'auto',
    fontFamily: 'var(--cdx-font-mono)',
    fontSize: 9.5,
    color: 'var(--cdx-turquesa)',
    background: 'var(--cdx-turq-wash)',
    padding: '2px 7px',
    borderRadius: 999,
  },
  mealLabel: { margin: 0, fontSize: 13.5, color: 'var(--cdx-ink)', lineHeight: 1.45 },
  mealNote: { margin: '4px 0 0', fontSize: 11.5, color: 'var(--cdx-ink-faint)', fontStyle: 'italic', lineHeight: 1.4 },
};

export default SeasonalWeekPlanner;
